import type { Client } from './client';
import type * as T from './types';

const enc = encodeURIComponent;

/**
 * The sign-in, second-factor and session routes both realms serve, typed by
 * the realm's `/auth/me` shape.
 */
function authEndpoints<Me>(c: Client) {
  return {
    login: (b: T.LoginRequest) => c.post<T.LoginResponse>('/auth/login', b),
    verifyTotp: (b: T.CodeRequest) => c.post<Me>('/auth/mfa/totp', b),
    webauthnLoginBegin: () => c.post<T.WebAuthnBeginResponse>('/auth/webauthn/login/begin', {}),
    webauthnLoginFinish: (b: T.CredentialRequest) => c.post<Me>('/auth/webauthn/login/finish', b),
    me: () => c.get<Me>('/auth/me'),
    logout: () => c.post<void>('/auth/logout'),
    // Second-factor enrollment: a full session, or (portal) an mfa-stage session when the
    // customer requires MFA and the user has no second factor yet.
    totpEnrollBegin: () => c.post<T.TotpEnrollBeginResponse>('/auth/totp/enroll/begin', {}),
    totpEnrollFinish: (b: T.CodeRequest) => c.post<Me>('/auth/totp/enroll/finish', b),
    webauthnRegisterBegin: () =>
      c.post<T.WebAuthnBeginResponse>('/auth/webauthn/register/begin', {}),
    webauthnRegisterFinish: (b: T.CredentialRequest) =>
      c.post<Me>('/auth/webauthn/register/finish', b),
    deleteCredential: (id: string) => c.delete<void>(`/auth/webauthn/credentials/${enc(id)}`),
    verifyRecoveryCode: (b: T.CodeRequest) => c.post<Me>('/auth/mfa/recovery', b),
    listCredentials: () => c.get<T.Page<T.Passkey>>('/auth/webauthn/credentials'),
    // A fresh second factor (10 min) for deleteCredential, generateRecoveryCodes, and
    // totpEnroll*/webauthnRegister* once the user has a factor; 403 reauth_required without one.
    reauthTotp: (b: T.CodeRequest) => c.post<void>('/auth/reauth/totp', b),
    reauthWebAuthnBegin: () => c.post<T.WebAuthnBeginResponse>('/auth/reauth/webauthn/begin', {}),
    reauthWebAuthnFinish: (b: T.CredentialRequest) =>
      c.post<void>('/auth/reauth/webauthn/finish', b),
    generateRecoveryCodes: () => c.post<T.RecoveryCodesResponse>('/auth/recovery-codes', {}),
    trustedBrowsers: () => c.get<T.Page<T.TrustedBrowser>>('/auth/trusted-browsers'),
    revokeTrustedBrowser: (id: string) => c.delete<void>(`/auth/trusted-browsers/${enc(id)}`),
  };
}

/** Console API (console.<domain>/api). */
export function consoleApi(c: Client) {
  return {
    auth: {
      ...authEndpoints<T.StaffMe>(c),
      setup: (b: T.SetupRequest) => c.post<T.LoginResponse>('/auth/setup', b),
      changePassword: (b: T.ChangePasswordRequest) => c.post<void>('/auth/password', b),
      sso: {
        /** The enabled Rethink providers the sign-in page offers; the browser starts one at startUrl(id). */
        providers: () => c.get<T.StaffSSOButtons>('/auth/sso/providers'),
        /** The enabled providers (Rethink's or a reseller's) that claim the email's domain. */
        discover: (b: { email: string }) => c.post<T.SSODiscoverResponse>('/auth/sso/discover', b),
        startUrl: (id: string, email?: string) => c.url(`/auth/sso/${enc(id)}/start`, { email }),
      },
      // The step-up link a first SSO sign-in for an existing user lands on. Every call
      // reads the __Host-central_console_sso cookie; without a live link it is 401 link_expired.
      link: {
        status: () => c.get<T.LinkStatus>('/auth/link'),
        password: (b: { password: string }) =>
          c.post<T.LinkPasswordResponse>('/auth/link/password', b),
        totp: (b: T.LinkTotpRequest) => c.post<T.StaffMe>('/auth/link/totp', b),
        webauthnBegin: () => c.post<T.WebAuthnBeginResponse>('/auth/link/webauthn/begin', {}),
        webauthnFinish: (b: T.LinkWebAuthnFinishRequest) =>
          c.post<T.StaffMe>('/auth/link/webauthn/finish', b),
      },
    },
    /**
     * Owner writes, Owner and Admin read: the providers console users sign in through.
     * list(): every one; list(''): Rethink's; list(id): that reseller's.
     */
    staffSso: {
      list: (resellerId?: string) =>
        c.get<T.Page<T.StaffSSOProvider>>(
          resellerId === undefined
            ? '/staff-sso-providers'
            : `/staff-sso-providers?resellerId=${enc(resellerId)}`,
        ),
      create: (b: T.StaffSSOProviderRequest) =>
        c.post<T.StaffSSOProvider>('/staff-sso-providers', b),
      get: (id: string) => c.get<T.StaffSSOProvider>(`/staff-sso-providers/${enc(id)}`),
      update: (id: string, b: T.StaffSSOProviderRequest) =>
        c.patch<T.StaffSSOProvider>(`/staff-sso-providers/${enc(id)}`, b),
      delete: (id: string) => c.delete<void>(`/staff-sso-providers/${enc(id)}`),
    },
    staff: {
      /** Owner: the user's link to one staff SSO provider is removed. */
      unlinkSso: (id: string, providerId: string) =>
        c.delete<T.StaffUser>(`/staff/${enc(id)}/sso/${enc(providerId)}`),
      /** By email; `resellerId` narrows to that reseller's users. */
      list: (q: { resellerId?: string } = {}) => c.get<T.Page<T.StaffUser>>('/staff', q),
      create: (b: T.CreateStaffRequest) => c.post<T.CreateStaffResponse>('/staff', b),
      update: (id: string, b: T.UpdateStaffRequest) => c.patch<T.StaffUser>(`/staff/${enc(id)}`, b),
      /** Owner: a new setup link (password reset); second factors are kept. */
      setupLink: (id: string) => c.post<T.SetupLinkResponse>(`/staff/${enc(id)}/setup-link`, {}),
      /** Owner, for another user: a setup link that also removes their second factors. */
      recoveryLink: (id: string) =>
        c.post<T.SetupLinkResponse>(`/staff/${enc(id)}/recovery-link`, {}),
    },
    overview: () => c.get<T.Overview>('/overview'),
    /** Owner and Admin: the mail transport and inbox settings. */
    settings: {
      get: () => c.get<T.Settings>('/settings'),
      /** Omitted keys are unchanged; an omitted secret keeps the stored one. */
      update: (b: T.SettingsRequest) => c.patch<T.Settings>('/settings', b),
      /** Sends a test email now through the saved transport. */
      testMail: (b: T.MailTestRequest) => c.post<T.MailTestResponse>('/settings/mail/test', b),
    },
    /** The signed-in user's own email notification preferences. */
    notificationPrefs: {
      get: () => c.get<T.NotificationPreferences>('/me/notifications'),
      update: (b: T.NotificationPreferencesRequest) =>
        c.patch<T.NotificationPreferences>('/me/notifications', b),
    },
    audit: {
      list: (q: T.AuditListQuery) => c.get<T.Page<T.AuditEvent>>('/audit', { ...q }),
    },
    customers: {
      /** `resellerId` is a reseller's id, or `none` for the customers Rethink supports directly. */
      list: (q: {
        query?: string;
        status?: T.CustomerStatus;
        resellerId?: string;
        cursor?: string;
        limit?: number;
      }) => c.get<T.Page<T.CustomerSummary>>('/customers', q),
      get: (id: string) => c.get<T.Customer>(`/customers/${enc(id)}`),
      create: (b: T.CreateCustomerRequest) => c.post<T.Customer>('/customers', b),
      update: (id: string, b: T.UpdateCustomerRequest) =>
        c.patch<T.Customer>(`/customers/${enc(id)}`, b),
      counts: (id: string) => c.get<T.CustomerCounts>(`/customers/${enc(id)}/counts`),
      users: (id: string) => c.get<T.PortalTeam>(`/customers/${enc(id)}/users`),
      updateUser: (id: string, userId: string, b: T.UpdateTeamUserRequest) =>
        c.patch<T.TeamUser>(`/customers/${enc(id)}/users/${enc(userId)}`, b),
      invite: (id: string, b: T.CreateInviteRequest) =>
        c.post<T.InviteResponse>(`/customers/${enc(id)}/invites`, b),
      sso: {
        list: (customerId: string) =>
          c.get<T.Page<T.SSOProvider>>(`/customers/${enc(customerId)}/sso`),
        create: (customerId: string, b: T.SSOProviderRequest) =>
          c.post<T.SSOProvider>(`/customers/${enc(customerId)}/sso`, b),
        get: (customerId: string, id: string) =>
          c.get<T.SSOProvider>(`/customers/${enc(customerId)}/sso/${enc(id)}`),
        update: (customerId: string, id: string, b: T.SSOProviderRequest) =>
          c.patch<T.SSOProvider>(`/customers/${enc(customerId)}/sso/${enc(id)}`, b),
        delete: (customerId: string, id: string) =>
          c.delete<void>(`/customers/${enc(customerId)}/sso/${enc(id)}`),
      },
    },
    catalogue: {
      features: () => c.get<T.Page<T.Feature>>('/features'),
      createFeature: (b: T.CreateFeatureRequest) => c.post<T.Feature>('/features', b),
      updateFeature: (id: string, b: T.UpdateFeatureRequest) =>
        c.patch<T.Feature>(`/features/${enc(id)}`, b),
      /** 409 feature_in_use while a license lists it, feature_in_product while a product does. */
      deleteFeature: (id: string, b: T.DeleteFeatureRequest) =>
        c.request<void>('DELETE', `/features/${enc(id)}`, b),
      products: () => c.get<T.Page<T.Product>>('/products'),
      createProduct: (b: T.CreateProductRequest) => c.post<T.Product>('/products', b),
      updateProduct: (id: string, b: T.UpdateProductRequest) =>
        c.patch<T.Product>(`/products/${enc(id)}`, b),
      /** 409 product_in_use while any license lists it. Its features stay. */
      deleteProduct: (id: string, b: T.DeleteProductRequest) =>
        c.request<void>('DELETE', `/products/${enc(id)}`, b),
    },
    licenses: {
      list: (q: T.LicenseListQuery) => c.get<T.Page<T.License>>('/licenses', { ...q }),
      get: (id: string) => c.get<T.License>(`/licenses/${enc(id)}`),
      create: (b: T.CreateLicenseRequest) => c.post<T.License>('/licenses', b),
      update: (id: string, b: T.UpdateLicenseRequest) =>
        c.patch<T.License>(`/licenses/${enc(id)}`, b),
      changes: (id: string) => c.get<T.Page<T.LicenseChange>>(`/licenses/${enc(id)}/changes`),
      activations: (id: string) => c.get<T.Page<T.Activation>>(`/licenses/${enc(id)}/activations`),
      issueOffline: (id: string, b: T.OfflineActivationsRequest) =>
        c.post<T.Page<T.Activation>>(`/licenses/${enc(id)}/offline-activations`, b),
      /** The offline license file: the bare JWT, saved as `<license key>.license`. */
      offlineFile: (id: string) => c.getFile(`/licenses/${enc(id)}/offline-file`),
    },
    activations: {
      revoke: (id: string, b: T.ActivationReasonRequest) =>
        c.post<T.Activation>(`/activations/${enc(id)}/revoke`, b),
      reinstate: (id: string, b: T.ActivationReasonRequest) =>
        c.post<T.Activation>(`/activations/${enc(id)}/reinstate`, b),
    },
    environments: {
      list: (q: T.EnvironmentListQuery) =>
        c.get<T.Page<T.EnvironmentSummary>>('/environments', { ...q }),
      get: (id: string) => c.get<T.Environment>(`/environments/${enc(id)}`),
      create: (b: T.CreateEnvironmentRequest) => c.post<T.Environment>('/environments', b),
      update: (id: string, b: T.UpdateEnvironmentRequest) =>
        c.patch<T.Environment>(`/environments/${enc(id)}`, b),
      enrollmentToken: (id: string) =>
        c.post<T.EnrollmentTokenResponse>(`/environments/${enc(id)}/enrollment-token`, {}),
      updateVisibility: (id: string, b: T.UpdateVisibilityRequest) =>
        c.patch<T.Environment>(`/environments/${enc(id)}/visibility`, b),
      approve: (id: string) => c.post<T.Environment>(`/environments/${enc(id)}/approve`, {}),
      uptime: (id: string, days = 90) =>
        c.get<T.EnvironmentUptime>(`/environments/${enc(id)}/uptime`, { days }),
      /** The 24 hours before `end` (RFC 3339), default now. */
      metrics: (id: string, q: { end?: string } = {}) =>
        c.get<T.EnvironmentMetrics>(`/environments/${enc(id)}/metrics`, q),
      /** The latest kept report, or with `at` (RFC 3339) the latest at or before it. */
      services: (id: string, q: { at?: string } = {}) =>
        c.get<T.EnvironmentServices>(`/environments/${enc(id)}/services`, q),
      versions: (id: string, q: { cursor?: string; limit?: number } = {}) =>
        c.get<T.Page<T.EnvironmentVersion>>(`/environments/${enc(id)}/versions`, q),
      rotateKey: (id: string) => c.post<T.Environment>(`/environments/${enc(id)}/rotate-key`, {}),
      revoke: (id: string, b: T.RevokeEnvironmentRequest) =>
        c.post<T.Environment>(`/environments/${enc(id)}/revoke`, b),
      archive: (id: string) => c.post<T.Environment>(`/environments/${enc(id)}/archive`, {}),
      unarchive: (id: string) => c.post<T.Environment>(`/environments/${enc(id)}/unarchive`, {}),
      setNextUpgrade: (id: string, b: T.SetNextUpgradeRequest) =>
        c.put<T.Environment>(`/environments/${enc(id)}/next-upgrade`, b),
      clearNextUpgrade: (id: string) =>
        c.delete<T.Environment>(`/environments/${enc(id)}/next-upgrade`),
      incidents: {
        list: (id: string, q: { cursor?: string; limit?: number } = {}) =>
          c.get<T.Page<T.Incident>>(`/environments/${enc(id)}/incidents`, q),
        create: (id: string, b: T.CreateIncidentRequest) =>
          c.post<T.Incident>(`/environments/${enc(id)}/incidents`, b),
        update: (id: string, incidentId: string, b: T.UpdateIncidentRequest) =>
          c.patch<T.Incident>(`/environments/${enc(id)}/incidents/${enc(incidentId)}`, b),
        remove: (id: string, incidentId: string) =>
          c.delete<void>(`/environments/${enc(id)}/incidents/${enc(incidentId)}`),
        postUpdate: (id: string, incidentId: string, b: T.PostIncidentUpdateRequest) =>
          c.post<T.Incident>(`/environments/${enc(id)}/incidents/${enc(incidentId)}/updates`, b),
      },
    },
    threads: {
      list: (q: T.ConsoleThreadListQuery) => c.get<T.Page<T.Thread>>('/threads', { ...q }),
      /** Staff start a conversation with a customer; the 201 is the new thread. */
      create: (b: T.StartThreadRequest) => c.post<T.ThreadDetail>('/threads', b),
      /** How many threads the list would return with the same filters. */
      count: (q: T.ConsoleThreadCountQuery) => c.get<T.ThreadCount>('/threads/count', { ...q }),
      unreadCount: () => c.get<T.UnreadCount>('/threads/unread-count'),
      get: (id: string, before?: string) =>
        c.get<T.ThreadDetail>(`/threads/${enc(id)}`, { before }),
      update: (id: string, b: T.UpdateThreadRequest) => c.patch<T.Thread>(`/threads/${enc(id)}`, b),
      /** One status or assignee change to up to 100 threads; a result per id, in order. */
      bulk: (b: T.BulkUpdateThreadsRequest) =>
        c.post<T.BulkUpdateThreadsResponse>('/threads/bulk', b),
      reply: (id: string, b: T.PostMessageRequest) =>
        c.post<T.Message>(`/threads/${enc(id)}/messages`, b),
      addNote: (id: string, b: T.PostMessageRequest) =>
        c.post<T.Message>(`/threads/${enc(id)}/notes`, b),
      markRead: (id: string) => c.post<void>(`/threads/${enc(id)}/read`),
      /** Both sides' watchers; userId "me" is the caller. */
      watchers: (id: string) => c.get<T.ThreadWatchers>(`/threads/${enc(id)}/watchers`),
      watch: (id: string, b: T.WatchRequest) =>
        c.post<T.ThreadWatchers>(`/threads/${enc(id)}/watchers`, b),
      unwatch: (id: string, side: T.WatchRequest['side'], userId: string) =>
        c.delete<T.ThreadWatchers>(`/threads/${enc(id)}/watchers/${enc(side)}/${enc(userId)}`),
      /** Rethink takes a tier 1 thread from its reseller; reason is required. */
      escalate: (id: string, b: T.TierRequest) =>
        c.post<T.Thread>(`/threads/${enc(id)}/escalate`, b),
      /** Hand a tier 2 thread back to its customer's reseller. */
      returnToTier1: (id: string, b: T.TierRequest) =>
        c.post<T.Thread>(`/threads/${enc(id)}/return`, b),
      /** The ticket report PDF, for a download link; notes includes internal notes. */
      reportUrl: (id: string, notes = false) =>
        c.url(`/threads/${enc(id)}/report.pdf${notes ? '?notes=true' : ''}`),
      crashReports: (id: string, q: T.CrashReportListQuery = {}) =>
        c.get<T.Page<T.CrashReportSummary>>(`/threads/${enc(id)}/crash-reports`, { ...q }),
      /** Tags in use starting with q, most used first: the tag editor's suggestions. */
      tags: (q = '') => c.get<T.ThreadTags>('/threads/tags', { q: q || undefined }),
      /** Links the ticket ref names ("GC-1042"), both ways. */
      link: (id: string, b: T.TicketRefRequest) => c.post<void>(`/threads/${enc(id)}/related`, b),
      unlink: (id: string, otherId: string) =>
        c.delete<void>(`/threads/${enc(id)}/related/${enc(otherId)}`),
      /** Closes the thread as a duplicate of the ticket ref names (same customer). */
      markDuplicate: (id: string, b: T.TicketRefRequest) =>
        c.post<T.Thread>(`/threads/${enc(id)}/duplicate`, b),
      /** Moves the thread's messages into the ticket ref names; answers that ticket. */
      merge: (id: string, b: T.TicketRefRequest) =>
        c.post<T.Thread>(`/threads/${enc(id)}/merge`, b),
    },
    reports: {
      /** The support report over tickets reported or resolved between from and to (UTC days, inclusive). */
      tickets: (q: T.TicketReportQuery) => c.get<T.TicketReport>('/reports/tickets', { ...q }),
      /** The report's tickets as CSV, for a download link. */
      ticketsCsvUrl: (q: T.TicketReportQuery) => c.url('/reports/tickets.csv', { ...q }),
      /** The report as a PDF, for a download link. */
      ticketsPdfUrl: (q: T.TicketReportQuery) => c.url('/reports/tickets.pdf', { ...q }),
    },
    /** Owner and Admin write; every Rethink role reads. */
    resellers: {
      list: () => c.get<T.Page<T.Reseller>>('/resellers'),
      get: (id: string) => c.get<T.Reseller>(`/resellers/${enc(id)}`),
      /** What the reseller looks after: customers, environments, tickets and team. */
      summary: (id: string) => c.get<T.ResellerSummary>(`/resellers/${enc(id)}/summary`),
      create: (b: T.ResellerRequest) => c.post<T.Reseller>('/resellers', b),
      /** Omitted fields are unchanged; '' clears a contact field. */
      update: (id: string, b: T.ResellerPatch) => c.patch<T.Reseller>(`/resellers/${enc(id)}`, b),
      /** 409 in_use while a customer or user belongs to it. */
      delete: (id: string) => c.delete<void>(`/resellers/${enc(id)}`),
      /** Uploads a PNG, JPEG or WebP logo of at most 256 KB; the server sniffs its type. */
      setLogo: (id: string, file: Blob) =>
        c.upload(c.url(`/resellers/${enc(id)}/logo`), file, {
          'Content-Type': file.type || 'application/octet-stream',
        }),
      deleteLogo: (id: string) => c.delete<T.Reseller>(`/resellers/${enc(id)}/logo`),
    },
    /**
     * The Reseller role's routes: only the reseller's own customers, their
     * dedicated environments and support threads. No licensing.
     */
    partner: {
      /** The reseller's home page: counts, attention list, latest tickets and release. */
      overview: () => c.get<T.PartnerOverview>('/partner/overview'),
      customers: {
        list: () => c.get<T.Page<T.PartnerCustomer>>('/partner/customers'),
        /** A customer of the caller's reseller; the profile only, no licensing. */
        create: (b: T.CreatePartnerCustomerRequest) =>
          c.post<T.PartnerCustomer>('/partner/customers', b),
        get: (id: string) => c.get<T.PartnerCustomerDetail>(`/partner/customers/${enc(id)}`),
        update: (id: string, b: T.UpdatePartnerCustomerRequest) =>
          c.patch<T.PartnerCustomer>(`/partner/customers/${enc(id)}`, b),
      },
      /**
       * The reseller's own users. Every reseller user lists them (the
       * enabled ones are who its threads can be assigned to); only its
       * reseller admins write.
       */
      team: {
        list: () => c.get<T.Page<T.PartnerUser>>('/partner/team'),
        /** The setup link is emailed and shown once. */
        create: (b: T.CreatePartnerUserRequest) =>
          c.post<T.CreatePartnerUserResponse>('/partner/team', b),
        /** 403 self_change for the caller's own demotion or disable; 409 last_reseller_admin. */
        update: (id: string, b: T.UpdatePartnerUserRequest) =>
          c.patch<T.PartnerUser>(`/partner/team/${enc(id)}`, b),
        setupLink: (id: string) =>
          c.post<T.SetupLinkResponse>(`/partner/team/${enc(id)}/setup-link`, {}),
      },
      environments: {
        list: (q: T.PartnerEnvironmentListQuery = {}) =>
          c.get<T.Page<T.PartnerEnvironment>>('/partner/environments', { ...q }),
        create: (b: T.CreatePartnerEnvironmentRequest) =>
          c.post<T.PartnerEnvironment>('/partner/environments', b),
        get: (id: string) => c.get<T.PartnerEnvironment>(`/partner/environments/${enc(id)}`),
        uptime: (id: string, q: { days?: number } = {}) =>
          c.get<T.EnvironmentUptime>(`/partner/environments/${enc(id)}/uptime`, q),
        services: (id: string) =>
          c.get<T.EnvironmentServices>(`/partner/environments/${enc(id)}/services`),
        /** One-time; shown once. Invalidates any unused token of the environment. */
        enrollmentToken: (id: string) =>
          c.post<T.EnrollmentTokenResponse>(
            `/partner/environments/${enc(id)}/enrollment-token`,
            {},
          ),
      },
      releases: (q: { limit?: number } = {}) => c.get<T.Page<T.Release>>('/partner/releases', q),
      /** The reseller's name, support contact and logo, as its customers see them. */
      profile: () => c.get<T.PartnerProfile>('/partner/profile'),
      reports: {
        /** GET /reports/tickets over the reseller's tier 1 threads. */
        support: (q: T.PartnerSupportReportQuery) =>
          c.get<T.TicketReport>('/partner/reports/support', { ...q }),
        supportCsvUrl: (q: T.PartnerSupportReportQuery) =>
          c.url('/partner/reports/support.csv', { ...q }),
        supportPdfUrl: (q: T.PartnerSupportReportQuery) =>
          c.url('/partner/reports/support.pdf', { ...q }),
      },
      threads: {
        list: (q: T.ConsoleThreadListQuery) =>
          c.get<T.Page<T.Thread>>('/partner/threads', { ...q }),
        /** Starts a tier 1 thread with one of the reseller's customers. */
        create: (b: T.StartThreadRequest) => c.post<T.ThreadDetail>('/partner/threads', b),
        count: (q: T.ConsoleThreadCountQuery) =>
          c.get<T.ThreadCount>('/partner/threads/count', { ...q }),
        /** Tier 1 threads with a message the reseller has not read. */
        unreadCount: () => c.get<T.UnreadCount>('/partner/threads/unread-count'),
        get: (id: string, before?: string) =>
          c.get<T.ThreadDetail>(`/partner/threads/${enc(id)}`, { before }),
        update: (id: string, b: T.UpdateThreadRequest) =>
          c.patch<T.Thread>(`/partner/threads/${enc(id)}`, b),
        reply: (id: string, b: T.PostMessageRequest) =>
          c.post<T.Message>(`/partner/threads/${enc(id)}/messages`, b),
        addNote: (id: string, b: T.PostMessageRequest) =>
          c.post<T.Message>(`/partner/threads/${enc(id)}/notes`, b),
        markRead: (id: string) => c.post<void>(`/partner/threads/${enc(id)}/read`),
        /** Hands a tier 1 thread to Rethink; reason is required. */
        escalate: (id: string, b: T.TierRequest) =>
          c.post<T.Thread>(`/partner/threads/${enc(id)}/escalate`, b),
        /** The ticket report PDF, for a download link; notes includes internal notes. */
        reportUrl: (id: string, notes = false) =>
          c.url(`/partner/threads/${enc(id)}/report.pdf${notes ? '?notes=true' : ''}`),
      },
    },
    crashReports: {
      get: (id: string) => c.get<T.CrashReport>(`/crash-reports/${enc(id)}`),
    },
    /** Saved responses for the thread composer; Owner and Admin write them. */
    cannedReplies: {
      /** By name; `kind` keeps those for that thread kind and those for every kind. */
      list: (q: { q?: string; kind?: T.ThreadKind } = {}) =>
        c.get<T.Page<T.CannedReply>>('/canned-replies', { ...q }),
      create: (b: T.CreateCannedReplyRequest) => c.post<T.CannedReply>('/canned-replies', b),
      update: (id: string, b: T.UpdateCannedReplyRequest) =>
        c.patch<T.CannedReply>(`/canned-replies/${enc(id)}`, b),
      delete: (id: string) => c.delete<void>(`/canned-replies/${enc(id)}`),
    },
    announcements: {
      list: (q: { limit?: number } = {}) =>
        c.get<T.Page<T.AnnouncementSummary>>('/announcements', q),
      get: (id: string) => c.get<T.Announcement>(`/announcements/${enc(id)}`),
      create: (b: T.CreateAnnouncementRequest) => c.post<T.Announcement>('/announcements', b),
      update: (id: string, b: T.UpdateAnnouncementRequest) =>
        c.patch<T.Announcement>(`/announcements/${enc(id)}`, b),
      delete: (id: string) => c.delete<void>(`/announcements/${enc(id)}`),
      publish: (id: string, b: T.PublishAnnouncementRequest = {}) =>
        c.post<T.PublishAnnouncementResponse>(`/announcements/${enc(id)}/publish`, b),
      preview: (b: T.AudiencePreviewRequest) =>
        c.post<T.AudiencePreview>('/announcements/preview', b),
      receipts: (id: string) => c.get<T.AnnouncementReceipts>(`/announcements/${enc(id)}/receipts`),
    },
    releases: {
      list: (q: { limit?: number } = {}) => c.get<T.Page<T.Release>>('/releases', q),
      get: (id: string) => c.get<T.Release>(`/releases/${enc(id)}`),
      create: (b: T.CreateReleaseRequest) => c.post<T.CreateReleaseResponse>('/releases', b),
      update: (id: string, b: T.UpdateReleaseRequest) =>
        c.patch<T.Release>(`/releases/${enc(id)}`, b),
      delete: (id: string) => c.delete<void>(`/releases/${enc(id)}`),
    },
    logs: {
      query: (q: T.LogQueryParams) => c.get<T.LogQueryResponse>('/logs/query', { ...q }),
      rate: (q: T.LogRateParams) => c.get<T.LogRateResponse>('/logs/rate', { ...q }),
      /** The caller's own saved searches. */
      views: {
        list: () => c.get<T.Page<T.SavedLogView>>('/logs/views'),
        create: (b: T.CreateSavedLogViewRequest) => c.post<T.SavedLogView>('/logs/views', b),
        delete: (id: string) => c.delete<void>(`/logs/views/${enc(id)}`),
      },
    },
  };
}

/** Portal API (portal.<domain>/api). Every call is scoped to the caller's customer. */
export function portalApi(c: Client) {
  return {
    auth: {
      ...authEndpoints<T.PortalMe>(c),
      /** Needs a fresh second factor when the user has one (403 reauth_required). */
      changePassword: (b: T.PortalChangePasswordRequest) => c.post<void>('/auth/password', b),
      /** 204 whatever the email names; a reset link is emailed to a real account. */
      forgotPassword: (b: T.ForgotPasswordRequest) => c.post<void>('/auth/password/forgot', b),
      /** The token is the reset link's fragment; 401 invalid_token once used or expired. */
      resetPassword: (b: T.ResetPasswordRequest) => c.post<void>('/auth/password/reset', b),
      sso: {
        discover: (b: { email: string }) => c.post<T.SSODiscoverResponse>('/auth/sso/discover', b),
      },
      // The step-up link a first SSO sign-in for an existing user lands on. Every call
      // reads the __Host-central_sso cookie; without a live link it is 401 link_expired.
      link: {
        status: () => c.get<T.LinkStatus>('/auth/link'),
        password: (b: { password: string }) =>
          c.post<T.LinkPasswordResponse>('/auth/link/password', b),
        totp: (b: T.LinkTotpRequest) => c.post<T.PortalMe>('/auth/link/totp', b),
        webauthnBegin: () => c.post<T.WebAuthnBeginResponse>('/auth/link/webauthn/begin', {}),
        webauthnFinish: (b: T.LinkWebAuthnFinishRequest) =>
          c.post<T.PortalMe>('/auth/link/webauthn/finish', b),
      },
    },
    home: () => c.get<T.PortalHome>('/home'),
    deployments: {
      list: () => c.get<T.Page<T.DeploymentCard>>('/deployments'),
      get: (id: string) => c.get<T.DeploymentDetail>(`/deployments/${enc(id)}`),
      uptime: (id: string, days = 90) =>
        c.get<T.EnvironmentUptime>(`/deployments/${enc(id)}/uptime`, { days }),
      incidents: (id: string) =>
        c.get<T.Page<T.DeploymentIncident>>(`/deployments/${enc(id)}/incidents`),
    },
    releases: {
      list: (q: { limit?: number } = {}) => c.get<T.PortalReleases>('/releases', q),
    },
    licenses: {
      list: () => c.get<T.PortalLicenses>('/licenses'),
      reveal: (id: string) => c.post<T.RevealLicenseResponse>(`/licenses/${enc(id)}/reveal`, {}),
      /** Owner only. The offline license file: the bare JWT, saved as `<license key>.license`. */
      offlineFile: (id: string) => c.getFile(`/licenses/${enc(id)}/offline-file`),
      /** Opens a renewal thread, or names the one already open. */
      requestRenewal: (id: string) =>
        c.post<T.RenewalRequestResponse>(`/licenses/${enc(id)}/renewal-request`, {}),
    },
    team: {
      get: () => c.get<T.PortalTeam>('/team'),
      updateUser: (id: string, b: T.UpdateTeamUserRequest) =>
        c.patch<T.TeamUser>(`/team/users/${enc(id)}`, b),
      invite: (b: T.CreateInviteRequest) => c.post<T.InviteResponse>('/team/invites', b),
      resendInvite: (id: string) => c.post<T.InviteResponse>(`/team/invites/${enc(id)}/resend`, {}),
      revokeInvite: (id: string) => c.delete<void>(`/team/invites/${enc(id)}`),
      acceptInvite: (b: T.AcceptInviteRequest) => c.post<void>('/auth/invite/accept', b),
    },
    settings: {
      get: () => c.get<T.OrganizationSettings>('/settings'),
      /** Owner. Turning requireMfa off needs a fresh second factor (403 reauth_required). */
      update: (b: T.OrganizationSettings) => c.put<T.OrganizationSettings>('/settings', b),
    },
    organization: () => c.get<T.PortalOrganization>('/organization'),
    /** The signed-in user's own email notification preferences. */
    notificationPrefs: {
      get: () => c.get<T.NotificationPreferences>('/me/notifications'),
      update: (b: T.NotificationPreferencesRequest) =>
        c.patch<T.NotificationPreferences>('/me/notifications', b),
    },
    sessions: {
      list: () => c.get<T.Page<T.PortalSession>>('/me/sessions'),
      revoke: (id: string) => c.delete<void>(`/me/sessions/${enc(id)}`),
      signOutOthers: () => c.post<T.SignOutOthersResponse>('/me/sessions/sign-out-others', {}),
    },
    threads: {
      list: (q: T.ThreadListQuery) => c.get<T.Page<T.PortalThread>>('/threads', { ...q }),
      /** How many threads the list would return with the same filters. */
      count: (q: T.ThreadCountQuery) => c.get<T.ThreadCount>('/threads/count', { ...q }),
      unreadCount: () => c.get<T.UnreadCount>('/threads/unread-count'),
      /**
       * What attaching logs (of services; all when empty) and a health snapshot
       * would send now. Only with `logs` are the logs read, as an audited query.
       */
      attachPreview: (environmentId: string, services: string[] = [], logs = false) =>
        c.get<T.AttachPreview>('/threads/attach-preview', {
          environmentId,
          services: logs && services.length ? services.join(',') : undefined,
          logs: logs ? 'true' : undefined,
        }),
      get: (id: string, before?: string) =>
        c.get<T.PortalThreadDetail>(`/threads/${enc(id)}`, { before }),
      create: (b: T.CreateThreadRequest) => c.post<T.PortalThreadDetail>('/threads', b),
      reply: (id: string, b: T.CustomerReplyRequest) =>
        c.post<T.PortalMessage>(`/threads/${enc(id)}/messages`, b),
      markRead: (id: string) => c.post<void>(`/threads/${enc(id)}/read`),
      /** The ticket report PDF (no internal notes), for a download link. */
      reportUrl: (id: string) => c.url(`/threads/${enc(id)}/report.pdf`),
      /** Closes the thread with a resolved event; a closed thread is unchanged. */
      resolve: (id: string) => c.post<T.PortalThread>(`/threads/${enc(id)}/resolve`),
      /** A closed thread waits on Rethink again, with a reopened event. */
      reopen: (id: string) => c.post<T.PortalThread>(`/threads/${enc(id)}/reopen`),
      /** Who is notified about the thread; userId "me" is the caller. */
      watchers: (id: string) => c.get<T.PortalThreadWatchers>(`/threads/${enc(id)}/watchers`),
      watch: (id: string, userId: string) =>
        c.post<T.PortalThreadWatchers>(`/threads/${enc(id)}/watchers`, { userId }),
      unwatch: (id: string, userId: string) =>
        c.delete<T.PortalThreadWatchers>(`/threads/${enc(id)}/watchers/${enc(userId)}`),
    },
    attachments: {
      presign: (b: T.PresignAttachmentRequest) => c.post<T.AttachmentUpload>('/attachments', b),
      /** Sends a reserved file to its same-origin uploadUrl. */
      upload: (up: T.AttachmentUpload, file: Blob) => c.upload(up.uploadUrl, file, up.headers),
    },
    notifications: {
      list: () => c.get<T.Page<T.PortalNotification>>('/notifications'),
      seen: (id: string) => c.post<void>(`/announcements/${enc(id)}/seen`, {}),
    },
    logs: {
      query: (q: T.LogQueryParams) => c.get<T.LogQueryResponse>('/logs/query', { ...q }),
    },
  };
}

export type ConsoleApi = ReturnType<typeof consoleApi>;
export type PortalApi = ReturnType<typeof portalApi>;
